import deepequal from "common/utils/deepequal";
import { productLog } from "common/utils/logging";
import { getWindowPersistentValue, setWindowPersistentValue } from "common/utils/persistentValue";
import { initTHREEJSRenderer } from "./initPhysicallyBasedRenderer";
import { initWebGLContext } from "./initWebGLContext";
import { SharedWebGL } from "./SharedWebGL";
import { WebGLContextReuse, type SharedWebGLOptions } from "./webGLContextTypes";

// Re-export for backwards compatibility
export { WebGLContextReuse } from "./webGLContextTypes";
export type { SharedWebGLOptions } from "./webGLContextTypes";

type ActiveOptions = Omit<SharedWebGLOptions, "reuse"> | undefined;

let activeSharedWebGL: SharedWebGL | undefined = undefined;
let activeOptions: ActiveOptions | undefined = undefined;

export function initSharedWebGL({
  throwOnGLError = false,
  logGLResourceUsage = false,
  reuse,
  onScreenRenderingDisabled = false,
}: SharedWebGLOptions): SharedWebGL {
  const options = { throwOnGLError, logGLResourceUsage, onScreenRenderingDisabled };
  if (
    activeSharedWebGL &&
    (reuse === WebGLContextReuse.ALWAYS_REUSE || deepequal(activeOptions, options)) &&
    reuse !== WebGLContextReuse.FORCE_NEW &&
    !activeSharedWebGL.glContext.isContextLost()
  ) {
    return activeSharedWebGL;
  }
  // Dispose any prior active shared WebGL context, including any lost from hot reloading.
  disposeActiveSharedWebGL();
  activeOptions = options;
  productLog("Initializing shared WebGL context");
  const { canvas, glContext, glContextAttributes, hasWebGL2, resourceTracker, resourceTrackerLogInterval } =
    initWebGLContext({
      framebufferOptions: {
        antialias: false,
        depthStencil: false,
      },

      throwOnGLError,
      logGLResourceUsage,
      label: "Main",
    });
  const renderer = initTHREEJSRenderer(canvas, glContext, glContextAttributes);

  // It's simpler to control the multi-viewport functionality by applying pixel ratio ourselves instead of setting it in three.js.
  renderer.setPixelRatio(1);

  const newSharedWebGL = new SharedWebGL(
    canvas,
    glContext,
    renderer,
    hasWebGL2,
    resourceTracker,
    resourceTrackerLogInterval,
    onScreenRenderingDisabled,
  );

  activeSharedWebGL = newSharedWebGL;
  setWindowPersistentValue("activeSharedWebGL", newSharedWebGL);
  return newSharedWebGL;
}

/**
 * To be used in tests to reset WebGL state.
 */
export function disposeActiveSharedWebGL() {
  const currentActiveSharedWebGL = activeSharedWebGL ?? getActiveSharedWebGL();
  if (currentActiveSharedWebGL) {
    productLog("Disposing active shared WebGL");
    currentActiveSharedWebGL.dispose();
    activeSharedWebGL = undefined;
    setWindowPersistentValue("activeSharedWebGL", undefined);
  }
}

export function getActiveSharedWebGL(): SharedWebGL | undefined {
  return getWindowPersistentValue<SharedWebGL | undefined>("activeSharedWebGL", undefined);
}
